import { Injectable, Injector, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { IDynamicInjectorProvider } from '@frontend/sports/host-app/sports-product/feature/non-root-token';
import { ClientConfigProductName, ProductMetadata, ProductService, WINDOW } from '@frontend/vanilla/core';
import { PLATFORM, linkedTransferState } from '@frontend/vanilla/ssr';

@Injectable({
    providedIn: 'root',
})
export class SportsProductService implements IDynamicInjectorProvider {
    private injector = inject(Injector);

    private productService = inject(ProductService);

    private readonly platform = inject(PLATFORM);
    private readonly _window = inject(WINDOW);

    readonly isSportsProductActiveTransferState = linkedTransferState('is-sports-product-active', false);

    readonly isSportsProductActive$ = toObservable(this.isSportsProductActiveTransferState);

    constructor() {
        if (!this.productService.isSingleDomainApp) {
            this.isSportsProductActiveTransferState.set(true);
            return;
        }

        this.platform.runOnServer(() => this.isSportsProductActiveTransferState.set(this.checkIsSportsProduct(this.productService.current)));

        this.productService.productChanged.subscribe((data) => {
            this.isSportsProductActiveTransferState.set(this.checkIsSportsProduct(data));
        });
    }

    getDynamicInjector(): Injector {
        if (!this.productService.isSingleDomainApp) {
            return this.injector;
        }

        const currentInjector = this.productService.current.injector;

        const sportsProduct = this.productService.getMetadata(ClientConfigProductName.SPORTS);

        if (!sportsProduct.isRegistered) {
            console.error(`Attempted to use sports product injector but sports product was not registered.`);

            return currentInjector;
        }

        return sportsProduct.injector;
    }

    activateSports(): void {
        this.productService.setActive(ClientConfigProductName.SPORTS);

        this._window.dispatchEvent(new CustomEvent('vnapp:productswitch:activate', { detail: { product: ClientConfigProductName.SPORTS } }));
    }

    deactivateSports(): void {
        this._window.dispatchEvent(new CustomEvent('vnapp:productswitch:deactivate', { detail: { product: ClientConfigProductName.SPORTS } }));
    }

    get isSingleDomain(): boolean {
        return this.productService.isSingleDomainApp;
    }

    get sportsProductName(): string {
        return ClientConfigProductName.SPORTS;
    }

    private checkIsSportsProduct(metadata: ProductMetadata): boolean {
        return metadata.name === ClientConfigProductName.SPORTS;
    }
}
